import request from "../libs/request";

import { BuildingId } from "../types/building";
import { GoodsTypeId } from "../types/goods";
import { ProfileImage } from "../types/member";

import { Pageable } from "./talkServices";

const baseUrl = `${process.env.REACT_APP_API_URL}/v2/manager/goods/rental-stock`;

export type RentalStockCreateRequest = {
  goodsId: string;
  storeId: string;
  useStock: boolean;
  stockCount: number;
  isActive: boolean;
};

export type RentalStockSummary = {
  totalStockCount: number;
  inUseCount: number;
  lostDamagedCount: number;
  availableCount: number;
};

export const getStringFromRentalStockSummary = (target: string) =>
  ({
    totalStockCount: "전체",
    inUseCount: "신청/대여",
    lostDamagedCount: "분실/손상",
    availableCount: "대여가능",
  }[target]!);

export type RentalStockGoodsTypeId = Exclude<GoodsTypeId, "REQUEST" | "REFILL">;

export const RentalStockGoodsTypeStrings: Record<
  RentalStockGoodsTypeId,
  string
> = {
  UMBRELLA: "우산",
  TOOLS: "공구",
  BOOK: "책",
  ITEM: "물품",
  ETC: "기타",
};

export const getStringFromRentalStockGoodsTypeId = (
  target: RentalStockGoodsTypeId
) => RentalStockGoodsTypeStrings[target];

export const getKeyValueFromRentalStockGoodsTypeId = () => {
  return Object.entries(RentalStockGoodsTypeStrings).map(([key, value]) => ({
    value: key,
    label: value,
  }));
};

export type RentalStockItem = {
  stockId: string;
  goodsId: string;
  goodsTypeId: RentalStockGoodsTypeId;
  image: ProfileImage;
  goodsName: string;
  goodsDescription: string;
  stockSummary: RentalStockSummary;
  storeName: string;
  //TODO: 재고관리 필요 여부 추가
  createdBy: number;
  createdAt: string;
  modifiedBy: number;
  modifiedAt: string | null;
  isActive: boolean;
};

export type RentalStockListSortOrder =
  | "createdAt.desc"
  | "modifiedAt.desc"
  | "lostDamagedCount.desc";

export const getStringFromRentalStockListSortOrder = (target: string) =>
  ({
    "createdAt.desc": "최근 등록순",
    "modifiedAt.desc": "최근 재고 변경순",
    "lostDamagedCount.desc": "분실/손상 많은 순",
  }[target]!);

export type RentalStockListRequest = {
  page?: number;
  pageSize?: number;
  sort: RentalStockListSortOrder;
  storeId?: string;
};

export type RentalStockHistoryRequest = {
  page?: number;
  pageSize?: number;
  historyType: RentalStockHistoryType | null;
  stockId?: string;
  goodsId?: string;
  goodsName?: string;
  storeName?: string;
};

export type RentalStockListResponse = {
  pageable: Pageable;
  items: RentalStockItem[];
};

export type RentalStockHistoryType =
  | "TOTAL_ADD"
  | "TOTAL_MINUS"
  | "TOTAL_MODIFIED"
  | "LOST_DAMAGED"
  | "RESTORED";

export const getStringFromRentalStockHistoryType = (target: string) =>
  ({
    TOTAL_ADD: "재고 추가",
    TOTAL_MINUS: "재고 감소",
    TOTAL_MODIFIED: "총 재고 변경",
    LOST_DAMAGED: "분실/손상 처리",
    RESTORED: "분실/손상 복원",
  }[target]!);

export const getCountStringFromRentalStockHistoryType = (
  item: RentalStockHistoryItem
) => {
  let count = 0;
  if (item.historyType === "TOTAL_MODIFIED") count = item.modifiedCount;
  else if (item.historyType === "LOST_DAMAGED")
    count = item.lostDamagedCount * -1;
  else if (item.historyType === "RESTORED") count = item.restoredCount;

  return count === 0 ? 0 : count > 0 ? `+${count}` : `${count}`;
};

export type RentalStockHistoryItem = BuildingId & {
  goodsStockHistoryId: string;
  goodsServiceItemId: string;
  historyType: RentalStockHistoryType;
  goodsId: string;
  stockId: string;
  goodsName: string;
  buildingName: string;
  storeId: string;
  storeName: string;
  modifiedCount: number;
  lostDamagedCount: number;
  restoredCount: number;
  stockCount: number;
  contactNumber: string;
  leasedBy: number;
  leasedByName: string;
  leasedByMobileNumber: string;
  leasedByCompanyName: string;
  createdBy: number;
  createdByName: string;
  createdAt: string;
};

export type RentalStockHistorySummary = {
  totalCount: number;
  totalModifiedCount: number;
  lostDamagedCount: number;
  restoredCount: number;
};

export const getStringFromRentalStockHistorySummary = (target: string) =>
  ({
    totalCount: "전체",
    totalModifiedCount: "재고 변경",
    lostDamagedCount: "분실/손상",
    restoredCount: "복원",
  }[target]!);

export type RentalStockHistoryResponse = {
  summary: RentalStockHistorySummary;
  list: {
    pageable: Pageable;
    items: RentalStockHistoryItem[];
  };
};

export const createRentalStockRequest = async (
  params: RentalStockCreateRequest
) => request.post<RentalStockItem>(`${baseUrl}`, params);

export const patchRentalStockStockCount = async (
  stockId: string,
  updateCount: number
) =>
  request.patch<RentalStockItem>(`${baseUrl}/${stockId}/stock-count`, {
    updateCount,
  });

export const patchRentalStockRestore = async (
  stockId: string,
  updateCount: number
) =>
  request.patch<RentalStockItem>(`${baseUrl}/${stockId}/restore`, {
    updateCount,
  });

export const getRentalStockDetail = async (stockId: string) =>
  request.get<RentalStockItem>(`${baseUrl}/${stockId}`);

export const deleteRentalStock = async (stockId: string) =>
  request.delete<RentalStockItem>(`${baseUrl}/${stockId}`);

export const getRentalStockList = async (params: RentalStockListRequest) =>
  request.get<RentalStockListResponse>(`${baseUrl}/list`, { params });

export const getRentalStockTitleList = async (params: RentalStockListRequest) =>
  request.get<RentalStockListResponse>(`${baseUrl}/title/list`, { params });

export const getRentalStockHistory = async (
  params: RentalStockHistoryRequest
) => request.get<RentalStockHistoryResponse>(`${baseUrl}/history`, { params });

export type RentalItem = {
  goodsTypeId: RentalStockGoodsTypeId;
  goodsId: string;
  goodsName: string;
};

export type RentalItemListResponse = {
  items: RentalItem[];
};

export type RentalItemListRequest = {
  goodsTypeId: RentalStockGoodsTypeId;
  storeId: string;
};

export const getRentalItemList = async (params: RentalItemListRequest) =>
  request.get<RentalItemListResponse>(
    `${process.env.REACT_APP_API_URL}/v2/manager/goods/rental-item/list`,
    {
      params,
    }
  );

export type StoreInfo = BuildingId & {
  buildingName: string;
  storeId: string;
  storeName: string;
  serviceGroupId: string;
};

export type StoreListResponse = {
  items: StoreInfo[];
};

export const getStoreList = async () =>
  request.get<StoreListResponse>(`/group/service/group/member/store/list`);

export type VirtualNumberResponse = {
  virtualNumber: string;
};

export const getVirtualNumber = async (memberId: number) =>
  request.get<VirtualNumberResponse>(
    `${process.env.REACT_APP_API_URL}/v2/manager/member/${memberId}/virtual-number`
  );
